import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import AuthContext from './context/AuthContext';
import { TextField, Button, Box, Typography, Container, CircularProgress, Alert, Chip, Stack, Divider } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { glass, brand } from './theme';
import { SectionHeader } from './components/SectionHeader';

function Profile() {
    const [profile, setProfile] = useState(null);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { user } = useContext(AuthContext);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/api/profile/');
            setProfile(response.data);
        } catch (error) {
            setMessage('خطا در بارگذاری پروفایل.');
            setSeverity('error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchProfile();
        }
    }, [user]);

    const handleChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const updateData = {
                full_name: profile.full_name,
                national_code: profile.national_code,
                birth_date_str: profile.birth_date_str,
                birth_place: profile.birth_place,
                address: profile.address,
                bio: profile.bio,
                work_experience_years: profile.work_experience_years,
                specialties: profile.specialties,
            };

            await apiClient.patch('/api/profile/', updateData);
            setMessage('پروفایل با موفقیت به‌روزرسانی شد!');
            setSeverity('success');
        } catch (error) {
            setMessage('خطا در به‌روزرسانی پروفایل.');
            setSeverity('error');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !profile) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
            <Container component="main" maxWidth="md" sx={{ pt: 3 }}>
                <Button component={RouterLink} to="/dashboard" startIcon={<ArrowForwardIcon />} size="small" sx={{ mb: 1, color: 'text.secondary' }}>
                    بازگشت به داشبورد
                </Button>
                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 }, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', bgcolor: brand.primary, color: '#fff', fontWeight: 800,
                        fontSize: '1.4rem', flexShrink: 0,
                    }}>
                        {(profile.full_name || user?.phone_number || 'ک')[0]}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {profile.full_name || 'پروفایل من'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{user?.phone_number}</Typography>
                    </Box>
                    {user?.user_type === 'provider' && (
                        <Chip
                            color={profile.is_verified_by_admin ? 'primary' : 'default'}
                            label={profile.is_verified_by_admin ? 'تأییدشده توسط مدیریت' : 'در انتظار تأیید مدیریت'}
                        />
                    )}
                </Box>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 } }}>
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={4}>
                            <Box>
                                <SectionHeader icon={BadgeIcon} title="اطلاعات هویتی" />
                                <TextField fullWidth margin="dense" label="نام کامل" name="full_name" value={profile.full_name || ''} onChange={handleChange} sx={fieldSx} />
                                {user?.user_type === 'provider' && (
                                    <>
                                        <TextField fullWidth margin="dense" label="کد ملی" name="national_code" value={profile.national_code || ''} onChange={handleChange} sx={fieldSx} />
                                        <TextField fullWidth margin="dense" label="تاریخ تولد (مثال: ۱۳۶۵/۰۲/۱۵)" name="birth_date_str" value={profile.birth_date_str || ''} onChange={handleChange} sx={fieldSx} />
                                        <TextField fullWidth margin="dense" label="محل تولد" name="birth_place" value={profile.birth_place || ''} onChange={handleChange} sx={fieldSx} />
                                    </>
                                )}
                            </Box>

                            <Divider sx={{ borderColor: 'rgba(15,51,43,0.1)' }} />

                            <Box>
                                <SectionHeader icon={HomeIcon} title="آدرس" />
                                <TextField fullWidth margin="dense" label="آدرس محل سکونت" name="address" multiline rows={3} value={profile.address || ''} onChange={handleChange} sx={fieldSx} />
                            </Box>

                            {user?.user_type === 'provider' && (
                                <>
                                    <Divider sx={{ borderColor: 'rgba(15,51,43,0.1)' }} />
                                    <Box>
                                        <SectionHeader icon={WorkHistoryIcon} title="سوابق حرفه‌ای" subtitle="این بخش برای خانواده‌ها نمایش داده می‌شود" />
                                        <TextField fullWidth margin="dense" label="توضیحات بیوگرافی" name="bio" multiline rows={4} value={profile.bio || ''} onChange={handleChange} sx={fieldSx} />
                                        <TextField fullWidth margin="dense" label="تخصص‌ها" name="specialties" value={profile.specialties || ''} onChange={handleChange} helperText="تخصص‌ها را با کاما (,) جدا کنید" sx={fieldSx} />
                                        <TextField fullWidth margin="dense" label="سال سابقه کار" name="work_experience_years" type="number" value={profile.work_experience_years || 0} onChange={handleChange} sx={fieldSx} />
                                    </Box>
                                </>
                            )}

                            {message && <Alert severity={severity} sx={{ borderRadius: '12px' }}>{message}</Alert>}

                            <Button type="submit" variant="contained" size="large" disabled={saving}>
                                {saving ? <CircularProgress size={22} color="inherit" /> : 'ذخیره تغییرات'}
                            </Button>
                        </Stack>
                    </form>
                </Box>
            </Container>
        </Box>
    );
}

export default Profile;
